import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { DEFAULT_TENANT_SLUG, resolveTenantSlug } from '../lib/tenant';

const HOAContext = createContext(null);

const TenantState = ({ icon, title, message }) => (
  <main className="min-h-screen bg-[#F5F0E8] px-6 py-12 font-sans text-[#1A1A18] flex items-center justify-center">
    <section className="w-full max-w-md rounded-2xl bg-[#FBF7EF] border border-[#E2D6C4] warm-tactile-shadow p-8 text-center">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E7DDC4] border border-white/70">
        <span className="material-symbols-outlined text-3xl text-secondary">{icon}</span>
      </div>
      <h1 className="text-xl font-extrabold tracking-tight text-primary-dark mb-2">{title}</h1>
      <p className="text-sm font-medium leading-relaxed text-slate-500">{message}</p>
    </section>
  </main>
);

export const HOAProvider = ({ children }) => {
  const [hoa, setHoa] = useState(null);
  const [slug, setSlug] = useState(DEFAULT_TENANT_SLUG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadHOA = async () => {
      const resolvedSlug = resolveTenantSlug(window.location);
      setSlug(resolvedSlug);
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .rpc('get_hoa_by_slug', { p_slug: resolvedSlug });

      const loadedHoa = data && data.length > 0 ? data[0] : null;

      if (!isMounted) return;

      if (fetchError) {
        setError(fetchError);
        setHoa(null);
      } else {
        setHoa(loadedHoa);
      }

      setIsLoading(false);
    };

    loadHOA();

    return () => {
      isMounted = false;
    };
  }, []);

  // index.html is shared by every association; title the tab after this one.
  useEffect(() => {
    if (!hoa?.name) return;
    const location = hoa.settings?.location;
    document.title = location ? `${hoa.name} - ${location}` : hoa.name;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', `${hoa.name} community portal${location ? ` - ${location}` : ''}`);
  }, [hoa]);

  const value = useMemo(() => ({
    hoa,
    slug,
    isLoading,
    error,
    settings: hoa?.settings || {},
  }), [hoa, isLoading, error, slug]);

  if (error) {
    return (
      <TenantState
        icon="error"
        title="Community lookup unavailable"
        message="We could not load this community right now. Please try again in a moment."
      />
    );
  }

  // Only show the no-HOA state once loading has actually completed;
  // during the initial fetch, children render with hoa = null.
  if (!isLoading && !hoa) {
    return (
      <TenantState
        icon="location_off"
        title="Community not found"
        message={`No HOA is configured for "${slug}". Please check the web address or contact support.`}
      />
    );
  }

  return (
    <HOAContext.Provider value={value}>
      {children}
    </HOAContext.Provider>
  );
};

export const useHOA = () => {
  const context = useContext(HOAContext);
  if (!context) {
    throw new Error('useHOA must be used within an HOAProvider');
  }
  return context;
};

export default HOAContext;
